import orderBy from 'lodash.orderby';
import get from 'lodash.get';
import sumBy from 'lodash.sumby';
import { AppState } from 'services/types';
import {
    FertilizerEntityIds,
    FertilizerEventUuid,
    FertilizerEventTotalArea,
    FertilizerEvent
} from 'services/fertilizerEvents/types';
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';
import { selectEntities } from 'services/entities/selectors';
import { selectFertilizerEvents } from 'services/fertilizerEvents/selectors';
import { makeSelectQueryStringValue } from 'workspaces/react-router-selectors';
import { FERTILIZER_EVENT_COPY_MODAL } from 'scenes/Fertilizer/constants';
import { OwnProps } from './index';

type Props = {
    entityIds: FertilizerEntityIds;
    isCompleted?: boolean;
};

const selectEntityIds = (state: AppState, props: Props): FertilizerEntityIds =>
    props.entityIds;

const selectFertilizerEventUuid = (
    state: AppState,
    props: OwnProps
): FertilizerEventUuid | undefined => props.fertilizerEventUuid;

const selectTotalArea = createDeepEqualSelector(
    selectEntityIds,
    selectEntities,
    (entityIds, entities) => {
        if (entityIds && entityIds.length) {
            return entityIds.reduce((acc, cur): FertilizerEventTotalArea => {
                const totalArea = get(entities, [cur, 'totalArea']);
                const removals = get(entities, [cur, 'removals']);

                if (totalArea) {
                    const removed = sumBy(
                        removals,
                        (removal) => removal.totalArea || 0
                    );

                    return Number((acc + totalArea - removed).toFixed(2));
                }

                return Number(acc.toFixed(2));
            }, 0);
        }

        return 0;
    }
);

const selectTotalTrees = createDeepEqualSelector(
    selectEntityIds,
    selectEntities,
    (entityIds, entities) => {
        if (entityIds && entityIds.length) {
            return entityIds.reduce((acc, cur): number => {
                const totalTrees = get(entities, [cur, 'numberOfTrees']);
                const removals = get(entities, [cur, 'removals']);

                if (typeof totalTrees === 'number') {
                    const removed = sumBy(
                        removals,
                        (removal) => removal.numberOfTrees || 0
                    );

                    return acc + totalTrees - removed;
                }

                return acc;
            }, 0);
        }

        return 0;
    }
);

const selectEntitiesHaveNumberOfTrees = createDeepEqualSelector(
    selectEntityIds,
    selectEntities,
    (entityIds, entities) => {
        let hasNumberOfTreesOnAllEntities = true;
        if (entityIds && entityIds.length) {
            for (const entityId of entityIds) {
                const numberOfTrees = get(entities, [
                    entityId,
                    'numberOfTrees'
                ]);

                if (!numberOfTrees) {
                    hasNumberOfTreesOnAllEntities = false;
                    break;
                }
            }
        } else {
            return false;
        }

        return hasNumberOfTreesOnAllEntities;
    }
);

const selectInitialValues = createDeepEqualSelector(
    selectFertilizerEventUuid,
    selectFertilizerEvents,
    makeSelectQueryStringValue(FERTILIZER_EVENT_COPY_MODAL),
    makeSelectQueryStringValue('fertilizerEventUuid'),
    (
        fertilizerEventUuid,
        fertilizerEvents,
        isCopyFertilizerModalOpen,
        selectedFertilizerEventUuid
    ) => {
        if (fertilizerEventUuid) {
            const event = get(fertilizerEvents, fertilizerEventUuid);
            const fertilizerEvent = get(event, 'fertilizerEvent');

            const fertilizerEntityIds = fertilizerEvent.fertilizerEntities.map(
                ({ id }) => id
            );

            const fertilizerProducts = orderBy(
                fertilizerEvent.fertilizerProducts,
                'orderIndex'
            );

            const isCopyingExistingFertilizerEvent =
                isCopyFertilizerModalOpen && selectedFertilizerEventUuid;

            const { startDate, entityId } = event;

            const {
                applicationMethod,
                technicalPersonName,
                justification,
                operatorPersonId,
                supplier,
                comment
            } = fertilizerEvent;

            return {
                startDate,
                entityId: isCopyingExistingFertilizerEvent
                    ? undefined
                    : entityId,
                fertilizerEntityIds: isCopyingExistingFertilizerEvent
                    ? undefined
                    : fertilizerEntityIds,
                applicationMethod,
                technicalPersonName,
                justification,
                operatorPersonId,
                supplier,
                fertilizerProducts,
                comment
            };
        }

        return { fertilizerProducts: [{}] };
    }
);

const selectFertilizerEvent = createDeepEqualSelector(
    selectFertilizerEventUuid,
    selectFertilizerEvents,
    (fertilizerEventUuid, fertilizerEvents): FertilizerEvent | undefined => {
        if (fertilizerEventUuid) {
            return get(fertilizerEvents, fertilizerEventUuid);
        }

        return undefined;
    }
);

const selectFertilizerEventIsCompleted = createDeepEqualSelector(
    selectFertilizerEvent,
    (fertilizerEvent) => {
        if (!fertilizerEvent) {
            return false;
        }

        const completedAt = get(fertilizerEvent, ['jobs', 0, 'completedAt']);

        return Boolean(completedAt);
    }
);

export {
    selectEntityIds,
    selectFertilizerEventUuid,
    selectTotalArea,
    selectTotalTrees,
    selectInitialValues,
    selectEntitiesHaveNumberOfTrees,
    selectFertilizerEventIsCompleted
};
